import { StandardEvents } from '@shopify/events';

/**
 * Keeps a product discount notice in sync with the selected variant and purchase mode.
 */
class DiscountAutoAppliedNotice extends HTMLElement {
  /** @type {Element | null} */
  #eventTarget = null;

  /** @type {MutationObserver | null} */
  #purchaseModeObserver = null;

  connectedCallback() {
    this.#eventTarget = this.closest('.shopify-section, dialog');
    this.#eventTarget?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#observePurchaseMode();
    this.#render();
  }

  disconnectedCallback() {
    this.#eventTarget?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#purchaseModeObserver?.disconnect();
  }

  #observePurchaseMode() {
    this.#purchaseModeObserver?.disconnect();

    const variantPicker = this.#variantPicker;
    if (!variantPicker) return;

    this.#purchaseModeObserver = new MutationObserver(this.#render);
    this.#purchaseModeObserver.observe(variantPicker, {
      attributes: true,
      attributeFilter: ['data-purchase-mode'],
    });
  }

  get #variantPicker() {
    if (!this.#eventTarget) return null;

    const productId = this.dataset.productId;
    return (
      this.#eventTarget.querySelector(`variant-picker[data-product-id="${CSS.escape(productId ?? '')}"]`) ??
      this.#eventTarget.querySelector('variant-picker')
    );
  }

  #render = () => {
    const variantPicker = this.#variantPicker;
    const sellingPlanInput = this.#eventTarget?.querySelector(
      'form[data-type="add-to-cart-form"] [name="selling_plan"]'
    );
    const subscriptionMode =
      this.dataset.hasSellingPlan === 'true' &&
      (variantPicker?.getAttribute('data-purchase-mode') === 'subscription' ||
        (!variantPicker && sellingPlanInput instanceof HTMLInputElement && !sellingPlanInput.disabled));
    const discount = subscriptionMode ? this.dataset.subscriptionDiscount : this.dataset.oneTimeDiscount;
    const isPositive = subscriptionMode
      ? this.dataset.subscriptionPositive === 'true'
      : this.dataset.oneTimePositive === 'true';
    const amount = this.querySelector('[data-discount-amount]');

    if (amount) amount.textContent = discount ?? '';
    this.hidden = !isPositive && this.dataset.designMode !== 'true';
  };

  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        if (detail.newProduct) {
          this.dataset.productId = detail.newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const updatedNotice = detail.html.querySelector(
          `discount-auto-applied-notice[data-block-id="${CSS.escape(this.dataset.blockId ?? '')}"]`
        );
        if (!(updatedNotice instanceof HTMLElement)) return;

        for (const key of [
          'productId',
          'oneTimeDiscount',
          'oneTimePositive',
          'subscriptionDiscount',
          'subscriptionPositive',
          'hasSellingPlan',
        ]) {
          const value = updatedNotice.dataset[key];
          if (value === undefined) {
            delete this.dataset[key];
          } else {
            this.dataset[key] = value;
          }
        }

        this.#observePurchaseMode();
        this.#render();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[discount-auto-applied-notice] Update failed:', error);
      });
  };
}

if (!customElements.get('discount-auto-applied-notice')) {
  customElements.define('discount-auto-applied-notice', DiscountAutoAppliedNotice);
}
